/**
 * Toast: lightweight, auto-dismissing confirmation/error message. Used
 * for "Layout saved" confirmations and save-failure errors per the
 * editor polish brief — Save currently works silently, this gives it a
 * visible result either way.
 *
 * Deliberately a simple local component, not a global toast queue/portal
 * system — this app needs "one message at a time near the save action,"
 * not a notification center. Resist generalizing this further than that.
 */

import { useEffect, type ReactNode } from 'react';

export type ToastTone = 'success' | 'error';

interface ToastProps {
  message: string;
  tone: ToastTone;
  onDismiss: () => void;
  /** Auto-dismiss after this many ms. Set to 0 to require manual dismiss. */
  durationMs?: number;
}

export function Toast({ message, tone, onDismiss, durationMs = 3000 }: ToastProps) {
  useEffect(() => {
    if (durationMs <= 0) return;
    const timeout = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(timeout);
  }, [onDismiss, durationMs]);

  const toneClasses =
    tone === 'success'
      ? 'border-evergreen-300 bg-evergreen-50 text-evergreen-700'
      : 'border-danger bg-white text-danger';

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-center gap-2 rounded-lg border px-4 py-2 font-body text-sm shadow-elevated ${toneClasses}`}
    >
      <span>{tone === 'success' ? '\u2713' : '\u26A0'}</span>
      <span>{message}</span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="ml-1 text-xs opacity-60 hover:opacity-100"
      >
        {'\u00d7'}
      </button>
    </div>
  );
}

/**
 * ToastStack: fixed-position container for one-at-a-time toasts. Mounted
 * once near the root of a page that needs save feedback (EditorPage).
 */
export function ToastStack({ children }: { children: ReactNode }) {
  return (
    <div className="pointer-events-none fixed bottom-20 right-4 z-50 flex flex-col gap-2 sm:bottom-6">
      {children}
    </div>
  );
}
